/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * Local drafts of long forms, so a failed save never costs the user what they typed.
 *
 * Rules (see the callers for the policy side):
 * - key: `housien:draft:v1:{userId}:{agencyId}:{kind}:{id|new}`; ids never mix.
 * - text/number/select values only: File/Blob and media keys are stripped.
 * - every storage access is wrapped: storage may throw (blocked, private mode)
 *   and the form must work exactly the same without it.
 * - the draft is deleted only after a 2xx, on discard, on expiry and on signout.
 * - Known limitation: with two tabs open on the same form the last write wins.
 */

export const DRAFT_PREFIX = 'housien:draft:v1:';
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type DraftKind = 'property' | 'quick-client';

export interface DraftScope {
  userId?: string | null;
  agencyId?: string | null;
  kind: DraftKind;
  /** The edited record id; omitted for a new record. */
  id?: string | null;
}

export interface StoredDraft<T = unknown> {
  savedAt: number;
  baseUpdatedAt?: string;
  data: T;
}

export interface ReadOptions {
  ttlMs?: number;
  /** The record's `updated_at` on the server; a newer one invalidates the draft. */
  serverUpdatedAt?: string | null;
}

const MEDIA_KEY = /^(images?|photos?|videos?|documents?|files?|attachments?|pending.*)$/i;

/**
 * Builds the storage key for a form, or null when there is no user/agency scope.
 *
 * @param scope - Who, where and what is being edited.
 * @returns The key, or null.
 */
export function buildDraftKey(scope: DraftScope): string | null {
  if (!scope.userId || !scope.agencyId) return null;
  const id = scope.id || 'new';
  return `${DRAFT_PREFIX}${scope.userId}:${scope.agencyId}:${scope.kind}:${id}`;
}

/**
 * Whether a value is a File or Blob (never persisted).
 *
 * @param value - Any value.
 * @returns True for binary values.
 */
function isBinary(value: unknown): boolean {
  return (typeof File !== 'undefined' && value instanceof File)
    || (typeof Blob !== 'undefined' && value instanceof Blob);
}

/**
 * Whether a value is a non-null, non-array object.
 *
 * @param value - Any value.
 * @returns True for plain objects.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Sanitises a nested value: arrays drop binaries and functions, objects recurse.
 *
 * @param value - Any value.
 * @returns The JSON-safe value.
 */
function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.filter((v) => !isBinary(v) && typeof v !== 'function').map(sanitizeValue);
  }
  return isPlainObject(value) ? sanitizeDraftData(value) : value;
}

/**
 * Removes everything that must never be persisted: File/Blob, functions and
 * media keys (photos, videos, documents, pending uploads).
 *
 * @param data - The form values.
 * @returns A JSON-safe copy.
 */
export function sanitizeDraftData<T extends Record<string, unknown>>(data: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (MEDIA_KEY.test(key) || isBinary(value) || typeof value === 'function') continue;
    out[key] = sanitizeValue(value);
  }
  return out;
}

/**
 * Removes one draft; never throws.
 *
 * @param key - The exact draft key.
 */
export function clearDraft(key: string | null): void {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // storage unavailable: nothing to clear
  }
}

/**
 * Writes a draft; never throws.
 *
 * @param key - The exact draft key.
 * @param data - The form values (sanitised here).
 * @param options - Optional `baseUpdatedAt` of the record being edited.
 * @returns True when the draft was actually stored.
 */
export function writeDraft(
  key: string | null,
  data: Record<string, unknown>,
  options: { baseUpdatedAt?: string | null } = {},
): boolean {
  if (!key) return false;
  const value: StoredDraft = { savedAt: Date.now(), data: sanitizeDraftData(data) };
  if (options.baseUpdatedAt) value.baseUpdatedAt = options.baseUpdatedAt;
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether a parsed value has the stored-draft shape.
 *
 * @param value - The parsed JSON.
 * @returns True when it has a numeric `savedAt` and an object `data`.
 */
function isDraftShape(value: unknown): value is StoredDraft {
  return isPlainObject(value) && typeof value.savedAt === 'number' && isPlainObject(value.data);
}

/**
 * Parses a raw stored string into a draft.
 *
 * @param raw - The raw localStorage value.
 * @returns The draft, or null when missing or corrupt.
 */
function parseDraft(raw: string | null): StoredDraft | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isDraftShape(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Whether the draft predates the server record's last update.
 *
 * @param draft - The stored draft.
 * @param serverUpdatedAt - The server `updated_at`, if known.
 * @returns True when the server version is newer.
 */
function isOlderThanServer(draft: StoredDraft, serverUpdatedAt?: string | null): boolean {
  const server = serverUpdatedAt ? Date.parse(serverUpdatedAt) : NaN;
  return Number.isFinite(server) && server > draft.savedAt;
}

/**
 * Whether the draft is within its TTL and not older than the server record.
 *
 * @param draft - The stored draft.
 * @param options - TTL override and the server `updated_at`.
 * @returns True when the draft may be offered.
 */
function isUsable(draft: StoredDraft, options: ReadOptions): boolean {
  const expired = Date.now() - draft.savedAt > (options.ttlMs ?? DRAFT_TTL_MS);
  return !expired && !isOlderThanServer(draft, options.serverUpdatedAt);
}

/**
 * Reads the raw value for a key; never throws.
 *
 * @param key - The exact draft key.
 * @returns The raw string, or null.
 */
function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Reads a draft, deleting it when corrupt, expired or older than the server record.
 *
 * @param key - The exact draft key.
 * @param options - TTL override and the server's `updated_at`.
 * @returns The usable draft, or null.
 */
export function readDraft<T = Record<string, unknown>>(
  key: string | null,
  options: ReadOptions = {},
): StoredDraft<T> | null {
  if (!key) return null;
  const raw = readRaw(key);
  if (raw === null) return null;
  const draft = parseDraft(raw);
  if (draft && isUsable(draft, options)) return draft as StoredDraft<T>;
  clearDraft(key);
  return null;
}

/**
 * Removes every draft of every user and agency (signout).
 */
export function clearAllDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // storage unavailable: nothing to clear
  }
}
